/* eslint-disable import-extensions/file-extension-required */
import { graphql } from 'react-relay';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';

import type {
	useGetTeamAndSubanchorGetPersonalSpaceKeyQuery$data,
	useGetTeamAndSubanchorGetPersonalSpaceKeyQuery$variables,
} from './__generated__/useGetTeamAndSubanchorGetPersonalSpaceKeyQuery.graphql';
import type {
	useGetTeamAndSubanchorGetSubanchorsAGGQuery$data,
	useGetTeamAndSubanchorGetSubanchorsAGGQuery$variables,
} from './__generated__/useGetTeamAndSubanchorGetSubanchorsAGGQuery.graphql';

const useGetTeamAndSubanchorGetSubanchorsAGGQuery = graphql`
	query useGetTeamAndSubanchorGetSubanchorsAGGQuery($accountId: ID!) {
		growthUnifiedProfile_getUnifiedProfile(accountId: $accountId) {
			paidChannelContext {
				confluence {
					subAnchor
				}
			}
		}
	}
`;

const useGetTeamAndSubanchorGetPersonalSpaceKeyQuery = graphql`
	query useGetTeamAndSubanchorGetPersonalSpaceKeyQuery {
		user: confluence_atlassianUser(current: true) {
			id
			team
			confluence {
				hasPersonalSpace
				space {
					id
					key
				}
			}
		}
	}
`;

export const useGetTeamAndSubanchor = ({ skip = true }: { skip: boolean }) => {
	const { userId } = useSessionData();
	const {
		data: subAnchorData,
		loading: subAnchorLoading,
		error: subAnchorError,
	} = useQueryInterop<
		useGetTeamAndSubanchorGetSubanchorsAGGQuery$data,
		useGetTeamAndSubanchorGetSubanchorsAGGQuery$variables
	>(useGetTeamAndSubanchorGetSubanchorsAGGQuery, {
		variables: { accountId: userId! },
		client: getAGGClient(),
		skip,
	});

	const {
		data: personalSpaceKeyAndTeamData,
		loading: personalSpaceKeyAndTeamLoading,
		error: personalSpaceKeyAndTeamError,
	} = useQueryInterop<
		useGetTeamAndSubanchorGetPersonalSpaceKeyQuery$data,
		useGetTeamAndSubanchorGetPersonalSpaceKeyQuery$variables
	>(useGetTeamAndSubanchorGetPersonalSpaceKeyQuery, {
		skip,
	});

	const personalSpaceKey =
		(personalSpaceKeyAndTeamData?.user?.confluence?.hasPersonalSpace &&
			personalSpaceKeyAndTeamData?.user?.confluence?.space?.key) ||
		'';
	const teamType = personalSpaceKeyAndTeamData?.user?.team || '';
	const subAnchor =
		subAnchorData?.growthUnifiedProfile_getUnifiedProfile?.paidChannelContext?.confluence
			?.subAnchor || '';

	return {
		teamType,
		personalSpaceKey,
		subAnchor,
		personalSpaceKeyAndTeamLoading,
		personalSpaceKeyAndTeamError,
		subAnchorLoading,
		subAnchorError,
	};
};
